export type Option = { value: string; label: string };

export type TikTokObjectiveKey = 'REACH' | 'TRAFFIC' | 'VIDEO_VIEWS' | 'LEAD_GENERATION' | 'SALES';

export type TikTokCampaignSubtypeKey =
  | 'REACH_AUCTION'
  | 'TRAFFIC_MANUAL'
  | 'VIDEO_VIEWS_STANDARD'
  | 'SALES_WEBSITE'
  | 'LEAD_GENERATION_MANUAL';

export type TikTokDestinationKey = 'WEBSITE' | 'INSTANT_PAGE' | 'APP_ANDROID' | 'APP_IOS' | 'TIKTOK_SHOP';

export type TikTokAdFormatKey =
  | 'SINGLE_VIDEO'
  | 'SINGLE_IMAGE'
  | 'SPARK_ADS'
  | 'LIVE_SHOPPING_ADS'
  | 'PRODUCT_SHOPPING_ADS';

export type TikTokModuleKey =
  | 'catalog'
  | 'shopAdsType'
  | 'placements'
  | 'audience'
  | 'budgetSchedule'
  | 'biddingOptimization'
  | 'tracking'
  | 'identity'
  | 'music'
  | 'productInformation'
  | 'instantPage'
  | 'sparkPost'
  | 'search';

export type TikTokObjectiveConfig = {
  key: TikTokObjectiveKey;
  label: string;
  category: 'AWARENESS' | 'CONSIDERATION' | 'CONVERSION';
  description: string;
  apiObjectiveType: string;
  subtypes: TikTokCampaignSubtypeKey[];
  defaultDestination?: TikTokDestinationKey;
  destinations: TikTokDestinationKey[];
  optimizationGoals: Option[];
  bidStrategies: Record<string, Option[]>;
  adFormats: TikTokAdFormatKey[];
  visibleModules: TikTokModuleKey[];
  notes?: string[];
};

export type TikTokDestinationConfig = {
  key: TikTokDestinationKey;
  label: string;
  description: string;
  apiPromotionType: string;
  supportedInApi: boolean;
  visibleModules: TikTokModuleKey[];
  adFormats?: TikTokAdFormatKey[];
  requiresAppId?: boolean;
  requiresPixel?: boolean;
  notes?: string[];
};

export type TikTokCampaignSubtypeConfig = {
  key: TikTokCampaignSubtypeKey;
  objective: TikTokObjectiveKey;
  label: string;
  description: string;
  destination?: TikTokDestinationKey;
  hidesCampaignBudget?: boolean;
  hidesSplitTest?: boolean;
  hidesCampaignBudgetOptimization?: boolean;
  usesBudgetStrategy?: boolean;
  enablesSearchFields?: boolean;
  showsGmvMax?: boolean;
  showsIos14?: boolean;
};

export const TIKTOK_OBJECTIVE_GROUPS = [
  { key: 'AWARENESS', label: '品牌认知', objectives: ['REACH'] as TikTokObjectiveKey[] },
  { key: 'CONSIDERATION', label: '受众意向', objectives: ['TRAFFIC', 'VIDEO_VIEWS'] as TikTokObjectiveKey[] },
  { key: 'CONVERSION', label: '行为转化', objectives: ['LEAD_GENERATION', 'SALES'] as TikTokObjectiveKey[] },
] as const;

export const TIKTOK_DESTINATIONS: Record<TikTokDestinationKey, Option> = {
  WEBSITE: { value: 'WEBSITE', label: '网站' },
  INSTANT_PAGE: { value: 'INSTANT_PAGE', label: 'TikTok 即时页面' },
  APP_ANDROID: { value: 'APP_ANDROID', label: '应用' },
  APP_IOS: { value: 'APP_IOS', label: '网站和应用' },
  TIKTOK_SHOP: { value: 'TIKTOK_SHOP', label: 'TikTok 商店' },
};

export const TIKTOK_AD_FORMATS: Record<TikTokAdFormatKey, Option> = {
  SINGLE_VIDEO: { value: 'SINGLE_VIDEO', label: '单视频' },
  SINGLE_IMAGE: { value: 'SINGLE_IMAGE', label: '单图片' },
  SPARK_ADS: { value: 'SPARK_ADS', label: 'Spark 广告' },
  LIVE_SHOPPING_ADS: { value: 'LIVE_SHOPPING_ADS', label: '直播购物广告' },
  PRODUCT_SHOPPING_ADS: { value: 'PRODUCT_SHOPPING_ADS', label: '商品购物广告' },
};

export const TIKTOK_CAMPAIGN_SUBTYPE_CONFIG: Record<TikTokCampaignSubtypeKey, TikTokCampaignSubtypeConfig> = {
  REACH_AUCTION: {
    key: 'REACH_AUCTION',
    objective: 'REACH',
    label: '竞价覆盖',
    description: '高效覆盖的广告，可使用常规广告系列预算。',
  },
  TRAFFIC_MANUAL: {
    key: 'TRAFFIC_MANUAL',
    objective: 'TRAFFIC',
    label: '手动推广系列',
    description: '标准工作流，保留主要预算、版位、定向与出价控制。',
  },
  VIDEO_VIEWS_STANDARD: {
    key: 'VIDEO_VIEWS_STANDARD',
    objective: 'VIDEO_VIEWS',
    label: '视频播放量',
    description: '以视频观看为核心优化目标。',
  },
  SALES_WEBSITE: {
    key: 'SALES_WEBSITE',
    objective: 'SALES',
    label: '网站',
    description: '通过网站转化提升销量。',
    destination: 'WEBSITE',
  },
  LEAD_GENERATION_MANUAL: {
    key: 'LEAD_GENERATION_MANUAL',
    objective: 'LEAD_GENERATION',
    label: '手动推广系列',
    description: '标准线索收集工作流，通过落地页收集用户线索。',
  },
};

export const TIKTOK_OBJECTIVE_CONFIG: Record<TikTokObjectiveKey, TikTokObjectiveConfig> = {
  REACH: {
    key: 'REACH',
    label: '覆盖人数',
    category: 'AWARENESS',
    description: '向尽可能多的用户展示广告，重点看覆盖和展示。',
    apiObjectiveType: 'REACH',
    subtypes: ['REACH_AUCTION'],
    defaultDestination: 'WEBSITE',
    destinations: ['WEBSITE', 'INSTANT_PAGE'],
    optimizationGoals: [
      { value: 'REACH', label: '覆盖' },
      { value: 'SHOW', label: '展示' },
    ],
    bidStrategies: {
      REACH: [{ value: 'BID_TYPE_NO_BID', label: '自动 CPM' }],
      SHOW: [{ value: 'BID_TYPE_NO_BID', label: '自动 CPM' }],
    },
    adFormats: ['SINGLE_VIDEO', 'SINGLE_IMAGE', 'SPARK_ADS'],
    visibleModules: ['placements', 'audience', 'budgetSchedule', 'biddingOptimization', 'identity', 'instantPage'],
  },
  TRAFFIC: {
    key: 'TRAFFIC',
    label: '访问量',
    category: 'CONSIDERATION',
    description: '把用户带到网站、Instant Page 或 App，适合点击与落地页访问。',
    apiObjectiveType: 'TRAFFIC',
    subtypes: ['TRAFFIC_MANUAL'],
    defaultDestination: 'WEBSITE',
    destinations: ['WEBSITE', 'APP_ANDROID'],
    optimizationGoals: [
      { value: 'CLICK', label: '点击' },
      { value: 'TRAFFIC_LANDING_PAGE_VIEW', label: '落地页访问' },
      { value: 'ENGAGED_VIEW', label: '深度互动会话' },
    ],
    bidStrategies: {
      CLICK: [
        { value: 'BID_TYPE_CUSTOM', label: '手动出价' },
        { value: 'BID_TYPE_MAX_CONVERSION', label: '最大转化' },
      ],
      TRAFFIC_LANDING_PAGE_VIEW: [
        { value: 'BID_TYPE_CUSTOM', label: '手动出价' },
        { value: 'BID_TYPE_MAX_CONVERSION', label: '最大转化' },
      ],
      ENGAGED_VIEW: [
        { value: 'BID_TYPE_CUSTOM', label: '手动出价' },
        { value: 'BID_TYPE_MAX_CONVERSION', label: '最大转化' },
      ],
    },
    adFormats: ['SINGLE_VIDEO', 'SINGLE_IMAGE', 'SPARK_ADS'],
    visibleModules: ['placements', 'audience', 'budgetSchedule', 'biddingOptimization', 'tracking', 'identity', 'music', 'instantPage', 'search'],
  },
  VIDEO_VIEWS: {
    key: 'VIDEO_VIEWS',
    label: '视频播放量',
    category: 'CONSIDERATION',
    description: '优化视频观看与内容消费，素材以视频或 Spark 内容为主。',
    apiObjectiveType: 'VIDEO_VIEWS',
    subtypes: ['VIDEO_VIEWS_STANDARD'],
    defaultDestination: 'WEBSITE',
    destinations: ['WEBSITE'],
    optimizationGoals: [
      { value: 'ENGAGED_VIEW', label: '观看 6 秒' },
      { value: 'ENGAGED_VIEW_FIFTEEN', label: '观看 15 秒' },
    ],
    bidStrategies: {
      ENGAGED_VIEW: [{ value: 'BID_TYPE_CUSTOM', label: '出价控制' }],
      ENGAGED_VIEW_FIFTEEN: [{ value: 'BID_TYPE_CUSTOM', label: '出价控制' }],
    },
    adFormats: ['SINGLE_VIDEO', 'SPARK_ADS'],
    visibleModules: ['placements', 'audience', 'budgetSchedule', 'biddingOptimization', 'identity', 'sparkPost'],
  },
  LEAD_GENERATION: {
    key: 'LEAD_GENERATION',
    label: '线索收集',
    category: 'CONVERSION',
    description: '通过落地页收集用户线索，支持转化和点击优化。',
    apiObjectiveType: 'LEAD_GENERATION',
    subtypes: ['LEAD_GENERATION_MANUAL'],
    defaultDestination: 'WEBSITE',
    destinations: ['WEBSITE'],
    optimizationGoals: [
      { value: 'CONVERT', label: '转化' },
      { value: 'CLICK', label: '点击' },
    ],
    bidStrategies: {
      CONVERT: [
        { value: 'BID_TYPE_CUSTOM', label: '手动出价' },
        { value: 'BID_TYPE_MAX_CONVERSION', label: '最大转化' },
      ],
      CLICK: [
        { value: 'BID_TYPE_CUSTOM', label: '手动出价' },
        { value: 'BID_TYPE_MAX_CONVERSION', label: '最大转化' },
      ],
    },
    adFormats: ['SINGLE_VIDEO', 'SINGLE_IMAGE', 'SPARK_ADS'],
    visibleModules: [
      'placements', 'audience', 'budgetSchedule', 'biddingOptimization',
      'tracking', 'identity', 'music',
    ],
  },
  SALES: {
    key: 'SALES',
    label: '销量',
    category: 'CONVERSION',
    description: '通过网站转化提升商品销量，支持转化和 ROAS 优化。',
    apiObjectiveType: 'PRODUCT_SALES',
    subtypes: ['SALES_WEBSITE'],
    defaultDestination: 'WEBSITE',
    destinations: ['WEBSITE'],
    optimizationGoals: [
      { value: 'CONVERT', label: '转化' },
      { value: 'VALUE', label: '总收入' },
    ],
    bidStrategies: {
      CONVERT: [
        { value: 'BID_TYPE_CUSTOM', label: '手动出价' },
        { value: 'BID_TYPE_MAX_CONVERSION', label: '最大转化' },
        { value: 'BID_TYPE_TARGET_COST', label: '目标成本' },
      ],
      VALUE: [
        { value: 'BID_TYPE_MAX_CONVERSION', label: '最大化投放' },
        { value: 'BID_TYPE_TARGET_COST', label: '目标 ROAS' },
      ],
    },
    adFormats: ['SINGLE_VIDEO', 'SINGLE_IMAGE', 'SPARK_ADS'],
    visibleModules: ['catalog', 'placements', 'audience', 'budgetSchedule', 'biddingOptimization', 'tracking', 'identity', 'music', 'productInformation'],
  },
};

export const TIKTOK_DESTINATION_CONFIG: Record<TikTokDestinationKey, TikTokDestinationConfig> = {
  WEBSITE: {
    key: 'WEBSITE',
    label: '网站',
    description: '使用网站链接、Pixel 和落地页完成访问或转化。',
    apiPromotionType: 'WEBSITE',
    supportedInApi: true,
    visibleModules: ['placements', 'audience', 'budgetSchedule', 'biddingOptimization', 'tracking', 'identity', 'music', 'productInformation'],
    adFormats: ['SINGLE_VIDEO', 'SINGLE_IMAGE', 'SPARK_ADS'],
    requiresPixel: true,
  },
  INSTANT_PAGE: {
    key: 'INSTANT_PAGE',
    label: 'TikTok 即时页面',
    description: '以 TikTok 即时页面为落地页，当前仍以结构对齐为主。',
    apiPromotionType: 'INSTANT_PAGE',
    supportedInApi: false,
    visibleModules: ['placements', 'audience', 'budgetSchedule', 'biddingOptimization', 'identity', 'music', 'instantPage'],
    adFormats: ['SINGLE_VIDEO', 'SINGLE_IMAGE', 'SPARK_ADS'],
  },
  APP_ANDROID: {
    key: 'APP_ANDROID',
    label: '应用',
    description: '推广 App，并要求填写 App ID。',
    apiPromotionType: 'APP_ANDROID',
    supportedInApi: true,
    visibleModules: ['placements', 'audience', 'budgetSchedule', 'biddingOptimization', 'tracking', 'identity', 'music', 'productInformation'],
    adFormats: ['SINGLE_VIDEO', 'SINGLE_IMAGE', 'SPARK_ADS'],
    requiresAppId: true,
  },
  APP_IOS: {
    key: 'APP_IOS',
    label: '网站和应用',
    description: '同时覆盖网站和应用销量路径。',
    apiPromotionType: 'APP_IOS',
    supportedInApi: true,
    visibleModules: ['placements', 'audience', 'budgetSchedule', 'biddingOptimization', 'tracking', 'identity', 'music', 'productInformation'],
    adFormats: ['SINGLE_VIDEO', 'SINGLE_IMAGE', 'SPARK_ADS'],
    requiresAppId: true,
  },
  TIKTOK_SHOP: {
    key: 'TIKTOK_SHOP',
    label: 'TikTok 商店',
    description: '绑定 TikTok 商店商品源，适合商品销量与购物广告场景。',
    apiPromotionType: 'SHOP_PURCHASES',
    supportedInApi: true,
    visibleModules: ['catalog', 'shopAdsType', 'placements', 'audience', 'budgetSchedule', 'biddingOptimization', 'identity', 'productInformation'],
    adFormats: ['SINGLE_VIDEO', 'SINGLE_IMAGE', 'SPARK_ADS', 'LIVE_SHOPPING_ADS', 'PRODUCT_SHOPPING_ADS'],
  },
};

export function getObjectiveConfig(objective: string): TikTokObjectiveConfig {
  return TIKTOK_OBJECTIVE_CONFIG[(objective as TikTokObjectiveKey) || 'TRAFFIC'] ?? TIKTOK_OBJECTIVE_CONFIG.TRAFFIC;
}

export function getCampaignSubtypeConfig(subtype: string): TikTokCampaignSubtypeConfig | null {
  return TIKTOK_CAMPAIGN_SUBTYPE_CONFIG[subtype as TikTokCampaignSubtypeKey] ?? null;
}

export function getObjectiveSubtypeOptions(objective: TikTokObjectiveKey): Option[] {
  return TIKTOK_OBJECTIVE_CONFIG[objective].subtypes.map((key) => ({
    value: key,
    label: TIKTOK_CAMPAIGN_SUBTYPE_CONFIG[key].label,
  }));
}

export function getDestinationConfig(destination: string): TikTokDestinationConfig | null {
  return TIKTOK_DESTINATION_CONFIG[destination as TikTokDestinationKey] ?? null;
}

export function optionsFromValues<T extends string>(values: T[], dictionary: Record<T, Option>): Option[] {
  return values.map((value) => dictionary[value]);
}
